import React, { useState } from 'react';
import FormField from '../../../forms/components/FormField';
import ImageUploadProgress from '../../../forms/components/ImageUploadProgress';
import Alerts from '../../../../shared/components/ui/Alerts.jsx';
import { HttpError } from '../../../../services/http/httpClient.js';
import * as technicianService from '../../../../services/technicianService.js';
import { getUserFromToken } from '../../../../context/authContext';

const IMAGE_UPLOAD_LIMIT_EXCEEDED_MESSAGE = 'No es posible enviar el formulario, ha superado el limite de 20 imágenes.';

const isImageUploadLimitError = (error) => (
    error instanceof HttpError
        ? error.status === 406
        : error?.status === 406
);

/**
 * Modal de formulario dinámico para completar tareas
 * @param {Object} task - Tarea asociada
 * @param {Object} form - Formulario seleccionado
 * @param {boolean} isOpen - Si el modal está abierto
 * @param {Function} onClose - Callback para cerrar
 * @param {Function} onSubmit - Callback para enviar formulario
 */
function DynamicFormModal({ task, form, isOpen, onClose, onSubmit }) {
    const [fieldValues, setFieldValues] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploadingImages, setIsUploadingImages] = useState(false);
    const [imageUploadProgress, setImageUploadProgress] = useState(0);
    const [showAlert, setShowAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState('Aviso');
    const [alertContent, setAlertContent] = useState('');
    const [alertOnClose, setAlertOnClose] = useState(() => () => setShowAlert(false));

    const validateForm = () => {
        if (!form || !form.fields) return false;
        return technicianService.validateForm(form.fields, fieldValues);
    };

    const handleFieldChange = (index, value) => {
        setFieldValues(prev => ({
            ...prev,
            [index]: value
        }));
    };

    const finishAndClose = () => {
        setFieldValues({});
        setImageUploadProgress(0);
        setIsUploadingImages(false);
        setIsSubmitting(false);
        onSubmit();
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setAlertTitle('Campos Requeridos');
            setAlertContent('Por favor complete todos los campos requeridos antes de continuar.');
            setAlertOnClose(() => () => setShowAlert(false));
            setShowAlert(true);
            return;
        }

        setIsSubmitting(true);

        try {
            // Convertir los valores por índice a un objeto con nombres de campos
            const formData = {};
            const filesData = {};
            const imagesByField = {};

            form.fields.forEach((field, index) => {
                const value = fieldValues[index];

                if (field.type === 'file' || field.type === 'image') {
                    if (value && value.length > 0) {
                        const filesArray = Array.from(value);

                        filesData[field.name] = filesArray.map(file => ({
                            name: file.name,
                            size: file.size,
                            type: file.type
                        }));
                        formData[field.name] = `Archivos seleccionados: ${value.length}`;

                        if (field.type === 'image') {
                            imagesByField[field.name] = filesArray;
                        }
                    } else {
                        formData[field.name] = '';
                    }
                } else {
                    formData[field.name] = value || '';
                }
            });

            // Enviar formulario usando el servicio
            const result = await technicianService.submitFormResponse(task.id, {
                formId: form.id,
                formData: formData,
                filesData: filesData
            });

            const formResponseId = result?.formResponseId;
            const hasImages = Object.keys(imagesByField).length > 0;

            // Si hay imágenes, subirlas a la carpeta canónica por campo
            if (formResponseId && hasImages) {
                setIsUploadingImages(true);
                setImageUploadProgress(0);

                try {
                    const technicianEmail = getUserFromToken()?.email;

                    await technicianService.uploadImages(
                        imagesByField,
                        { formId: form.id, taskId: task.id, technicianEmail },
                        (progress) => setImageUploadProgress(progress)
                    );
                } catch (imageErr) {
                    console.error('Error al enviar imágenes:', imageErr);
                    setIsUploadingImages(false);
                    setAlertTitle('Error al subir imágenes');
                    setAlertContent('El formulario se guardó correctamente, pero ocurrió un error al subir las imágenes. Puedes intentar agregarlas nuevamente editando la respuesta.');
                    setAlertOnClose(() => () => {
                        setShowAlert(false);
                        finishAndClose();
                    });
                    setShowAlert(true);
                    return;
                } finally {
                    setIsUploadingImages(false);
                }
            }

            finishAndClose();

        } catch (err) {
            console.error('Error enviando formulario:', err);
            if (isImageUploadLimitError(err)) {
                setAlertTitle('Error');
                setAlertContent(IMAGE_UPLOAD_LIMIT_EXCEEDED_MESSAGE);
                setShowAlert(true);
                setIsSubmitting(false);
                return;
            }
            setAlertTitle('Error');
            setAlertContent('Error al enviar formulario. Por favor, inténtalo de nuevo.');
            setAlertOnClose(() => () => setShowAlert(false));
            setShowAlert(true);
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFieldValues({});
        setImageUploadProgress(0);
        setIsUploadingImages(false);
        setIsSubmitting(false);
        onClose();
    };

    if (!isOpen || !task || !form) return null;

    return (
        <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-[9999]">
            <div className="bg-surface rounded-lg shadow-xl w-[95vw] h-[90vh] max-w-4xl max-h-[800px] flex flex-col">
                {/* Header del modal */}
                <div className="flex justify-between items-center p-4 border-b border-line">
                    <h3 className="text-lg font-semibold text-content">
                        {form.name || 'Formulario de Actividad'} - {task.title}
                    </h3>
                    <button
                        onClick={handleClose}
                        className="text-content-muted hover:text-content text-xl font-bold"
                    >
                        ×
                    </button>
                </div>

                {/* Barra de progreso de carga de imágenes */}
                {isUploadingImages && (
                    <ImageUploadProgress progress={imageUploadProgress} />
                )}

                {/* Contenido del formulario */}
                <div className="flex-1 p-4 overflow-y-auto">
                    <div className="space-y-4">
                        <h4 className="text-lg font-medium text-content">{form.name}</h4>
                        {form.description && (
                            <p className="text-content-muted">{form.description}</p>
                        )}

                        {form.fields && form.fields.map((field, index) => (
                            <FormField
                                key={`${form.id}_${field.name}_${index}`}
                                field={field}
                                index={index}
                                value={fieldValues[index]}
                                onChange={handleFieldChange}
                                formId={form.id}
                            />
                        ))}
                    </div>
                </div>

                {/* Footer con botones */}
                <div className="p-4 border-t border-line flex justify-end space-x-3">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-content-muted hover:text-content transition-colors"
                        disabled={isSubmitting || isUploadingImages}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!validateForm() || isSubmitting || isUploadingImages}
                        className="px-4 py-2 bg-amber text-on-amber rounded-lg hover:bg-amber-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Enviando...' : isUploadingImages ? 'Subiendo imágenes...' : 'Enviar Formulario'}
                    </button>
                </div>
            </div>

            {/* Alertas */}
            {showAlert && (
                <Alerts
                    title={alertTitle}
                    onClose={alertOnClose}
                >
                    {alertContent}
                </Alerts>
            )}
        </div>
    );
}

export default DynamicFormModal;
